import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { checkResetToken, resetPassword, apiError } from "@/lib/api";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    checkResetToken(token).then((r) => setValid(r.valid)).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await resetPassword({ token, password, confirm_password: confirm });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const backLink = (label, testid) => (
    <Link to="/login" className="block text-center text-sm text-[#002FA7] hover:underline" data-testid={testid}>{label}</Link>
  );

  if (valid === null) {
    return <AuthShell title="Reset password" subtitle="Checking your link…"><Loader2 className="mx-auto animate-spin text-slate-400" /></AuthShell>;
  }
  if (!valid) {
    return (
      <AuthShell title="Link expired" subtitle="This reset link is invalid, already used, or older than 1 hour.">
        <div className="space-y-4" data-testid="reset-invalid">
          <Link to="/forgot-password" className="block text-center text-sm text-[#002FA7] hover:underline" data-testid="reset-request-new">Request a new link</Link>
          {backLink("Back to sign in", "reset-invalid-back")}
        </div>
      </AuthShell>
    );
  }
  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Sign in with your new password and your authenticator code.">
        <div className="space-y-4" data-testid="reset-success">
          <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
          {backLink("Go to sign in", "reset-go-login")}
        </div>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Choose a new password" subtitle="Minimum 8 characters. Your 2FA stays the same.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="label-caps">New password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" autoFocus data-testid="reset-password" />
        </div>
        <div>
          <Label className="label-caps">Confirm password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading || !password || !confirm} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="reset-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Update password
        </Button>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
